/**
 * カメラ周辺の符号付き距離場。
 * CollisionWorld の AABB / OBB を、各グリッド点で閉形式の sdBox の min として焼く。
 * 薄い形状だけ格子幅まで厚くし、サンプル点が面を跨いで抜けないようにする。
 */

import type { Obb } from './obb';
import { aabbIntersects, colliderBounds, type Aabb, type Collider } from './physics';
import type { Vec3 } from './types';

export interface VoxelVolume {
  origin: Vec3;
  extent: number;
  resolution: number;
}

export function voxelSizeOf(volume: VoxelVolume): number {
  return volume.extent / volume.resolution;
}

export function volumeIndex(volume: VoxelVolume, x: number, y: number, z: number): number {
  return x + y * volume.resolution + z * volume.resolution * volume.resolution;
}

export function volumeVoxelCount(resolution: number): number {
  return resolution * resolution * resolution;
}

/** 中心がカメラになる立方体。原点はボクセル境界にスナップして毎フレーム泳がないようにする。 */
export function volumeFromCenter(center: Vec3, extent: number, resolution: number): VoxelVolume {
  const size = extent / Math.max(resolution, 1);
  const half = extent / 2;
  return {
    origin: {
      x: Math.floor((center.x - half) / size) * size,
      y: Math.floor((center.y - half) / size) * size,
      z: Math.floor((center.z - half) / size) * size,
    },
    extent,
    resolution,
  };
}

export function volumeBounds(volume: VoxelVolume): Aabb {
  return {
    min: { ...volume.origin },
    max: {
      x: volume.origin.x + volume.extent,
      y: volume.origin.y + volume.extent,
      z: volume.origin.z + volume.extent,
    },
  };
}

export function volumesEqual(a: VoxelVolume, b: VoxelVolume, epsilon = 1e-6): boolean {
  return (
    a.resolution === b.resolution &&
    Math.abs(a.extent - b.extent) <= epsilon &&
    Math.abs(a.origin.x - b.origin.x) <= epsilon &&
    Math.abs(a.origin.y - b.origin.y) <= epsilon &&
    Math.abs(a.origin.z - b.origin.z) <= epsilon
  );
}

/** 半開区間のボクセル範囲。 */
export interface VoxelIndexBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  z0: number;
  z1: number;
}

export function fullVoxelIndexBox(resolution: number): VoxelIndexBox {
  return { x0: 0, x1: resolution, y0: 0, y1: resolution, z0: 0, z1: resolution };
}

function clipIndexBox(box: VoxelIndexBox, n: number): VoxelIndexBox | null {
  const x0 = Math.max(0, Math.min(n, box.x0 | 0));
  const x1 = Math.max(0, Math.min(n, box.x1 | 0));
  const y0 = Math.max(0, Math.min(n, box.y0 | 0));
  const y1 = Math.max(0, Math.min(n, box.y1 | 0));
  const z0 = Math.max(0, Math.min(n, box.z0 | 0));
  const z1 = Math.max(0, Math.min(n, box.z1 | 0));
  if (x0 >= x1 || y0 >= y1 || z0 >= z1) return null;
  return { x0, x1, y0, y1, z0, z1 };
}

/** 原点スナップ差分をボクセル単位の整数オフセットにする。解像度が違うと null。 */
export function voxelOriginShift(
  from: VoxelVolume,
  to: VoxelVolume
): { ox: number; oy: number; oz: number } | null {
  if (from.resolution !== to.resolution || Math.abs(from.extent - to.extent) > 1e-6) return null;
  const size = voxelSizeOf(from);
  if (size < 1e-8) return null;
  const ox = Math.round((to.origin.x - from.origin.x) / size);
  const oy = Math.round((to.origin.y - from.origin.y) / size);
  const oz = Math.round((to.origin.z - from.origin.z) / size);
  const tol = size * 1e-3;
  if (
    Math.abs(to.origin.x - from.origin.x - ox * size) > tol ||
    Math.abs(to.origin.y - from.origin.y - oy * size) > tol ||
    Math.abs(to.origin.z - from.origin.z - oz * size) > tol
  ) {
    return null;
  }
  return { ox, oy, oz };
}

/** シフト後に古い格子からコピーできない新規スラブ。 */
export function uncoveredVoxelRegions(
  resolution: number,
  ox: number,
  oy: number,
  oz: number
): VoxelIndexBox[] {
  const n = resolution;
  const regions: VoxelIndexBox[] = [];
  if (ox > 0) regions.push({ x0: n - ox, x1: n, y0: 0, y1: n, z0: 0, z1: n });
  else if (ox < 0) regions.push({ x0: 0, x1: -ox, y0: 0, y1: n, z0: 0, z1: n });
  if (oy > 0) regions.push({ x0: 0, x1: n, y0: n - oy, y1: n, z0: 0, z1: n });
  else if (oy < 0) regions.push({ x0: 0, x1: n, y0: 0, y1: -oy, z0: 0, z1: n });
  if (oz > 0) regions.push({ x0: 0, x1: n, y0: 0, y1: n, z0: n - oz, z1: n });
  else if (oz < 0) regions.push({ x0: 0, x1: n, y0: 0, y1: n, z0: 0, z1: -oz });
  return regions;
}

export function copyShiftedVoxelSdf(
  src: Float32Array,
  from: VoxelVolume,
  to: VoxelVolume
): Float32Array | null {
  const shift = voxelOriginShift(from, to);
  if (!shift) return null;
  const n = to.resolution;
  if (src.length !== volumeVoxelCount(n)) return null;
  if (Math.abs(shift.ox) >= n || Math.abs(shift.oy) >= n || Math.abs(shift.oz) >= n) return null;
  const dst = new Float32Array(src.length);
  dst.fill(to.extent);
  const { ox, oy, oz } = shift;
  const n2 = n * n;
  for (let z = 0; z < n; z++) {
    const sz = z + oz;
    if (sz < 0 || sz >= n) continue;
    for (let y = 0; y < n; y++) {
      const sy = y + oy;
      if (sy < 0 || sy >= n) continue;
      const dstRow = z * n2 + y * n;
      const srcRow = sz * n2 + sy * n;
      for (let x = 0; x < n; x++) {
        const sx = x + ox;
        if (sx < 0 || sx >= n) continue;
        dst[dstRow + x] = src[srcRow + sx];
      }
    }
  }
  return dst;
}

/** 軸平行ボックス。内部は負。 */
export function sdAabb(point: Vec3, box: Aabb): number {
  return sdBox(
    point.x - (box.min.x + box.max.x) * 0.5,
    point.y - (box.min.y + box.max.y) * 0.5,
    point.z - (box.min.z + box.max.z) * 0.5,
    (box.max.x - box.min.x) * 0.5,
    (box.max.y - box.min.y) * 0.5,
    (box.max.z - box.min.z) * 0.5
  );
}

/** 向き付きボックス。内部は負。 */
export function sdObb(point: Vec3, obb: Obb): number {
  const dx = point.x - obb.center.x;
  const dy = point.y - obb.center.y;
  const dz = point.z - obb.center.z;
  return sdBox(
    dx * obb.axes[0].x + dy * obb.axes[0].y + dz * obb.axes[0].z,
    dx * obb.axes[1].x + dy * obb.axes[1].y + dz * obb.axes[1].z,
    dx * obb.axes[2].x + dy * obb.axes[2].y + dz * obb.axes[2].z,
    obb.half.x,
    obb.half.y,
    obb.half.z
  );
}

function sdBox(px: number, py: number, pz: number, hx: number, hy: number, hz: number): number {
  const dx = Math.abs(px) - hx;
  const dy = Math.abs(py) - hy;
  const dz = Math.abs(pz) - hz;
  const ax = dx > 0 ? dx : 0;
  const ay = dy > 0 ? dy : 0;
  const az = dz > 0 ? dz : 0;
  const outside = Math.sqrt(ax * ax + ay * ay + az * az);
  const inside = Math.min(Math.max(dx, dy, dz), 0);
  return outside + inside;
}

function thicken(value: number, minHalf: number): number {
  return value < minHalf ? minHalf : value;
}

function stampAabbSdf(
  sdf: Float32Array,
  volume: VoxelVolume,
  box: Aabb,
  minHalf: number,
  region: VoxelIndexBox
): void {
  stampAlignedBox(
    sdf,
    volume,
    (box.min.x + box.max.x) * 0.5,
    (box.min.y + box.max.y) * 0.5,
    (box.min.z + box.max.z) * 0.5,
    thicken((box.max.x - box.min.x) * 0.5, minHalf),
    thicken((box.max.y - box.min.y) * 0.5, minHalf),
    thicken((box.max.z - box.min.z) * 0.5, minHalf),
    region
  );
}

function stampAlignedBox(
  sdf: Float32Array,
  volume: VoxelVolume,
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  region: VoxelIndexBox
): void {
  const n = volume.resolution;
  const size = voxelSizeOf(volume);
  const ox = volume.origin.x + size * 0.5;
  const oy = volume.origin.y + size * 0.5;
  const oz = volume.origin.z + size * 0.5;
  const n2 = n * n;
  for (let z = region.z0; z < region.z1; z++) {
    const pz = oz + z * size;
    const dz = Math.abs(pz - cz) - hz;
    const az = dz > 0 ? dz : 0;
    const zBase = z * n2;
    for (let y = region.y0; y < region.y1; y++) {
      const py = oy + y * size;
      const dy = Math.abs(py - cy) - hy;
      const ay = dy > 0 ? dy : 0;
      const yBase = zBase + y * n;
      for (let x = region.x0; x < region.x1; x++) {
        const dx = Math.abs(ox + x * size - cx) - hx;
        const ax = dx > 0 ? dx : 0;
        const d = Math.sqrt(ax * ax + ay * ay + az * az) + Math.min(Math.max(dx, dy, dz), 0);
        const i = yBase + x;
        if (d < sdf[i]) sdf[i] = d;
      }
    }
  }
}

function stampObbSdf(
  sdf: Float32Array,
  volume: VoxelVolume,
  obb: Obb,
  minHalf: number,
  region: VoxelIndexBox
): void {
  const n = volume.resolution;
  const size = voxelSizeOf(volume);
  const ox = volume.origin.x + size * 0.5;
  const oy = volume.origin.y + size * 0.5;
  const oz = volume.origin.z + size * 0.5;
  const hx = thicken(obb.half.x, minHalf);
  const hy = thicken(obb.half.y, minHalf);
  const hz = thicken(obb.half.z, minHalf);
  const ax = obb.axes[0];
  const ay = obb.axes[1];
  const az = obb.axes[2];
  const cx = obb.center.x;
  const cy = obb.center.y;
  const cz = obb.center.z;
  const n2 = n * n;
  for (let z = region.z0; z < region.z1; z++) {
    const pz = oz + z * size - cz;
    const zBase = z * n2;
    for (let y = region.y0; y < region.y1; y++) {
      const py = oy + y * size - cy;
      const yBase = zBase + y * n;
      for (let x = region.x0; x < region.x1; x++) {
        const px = ox + x * size - cx;
        const d = sdBox(
          px * ax.x + py * ax.y + pz * ax.z,
          px * ay.x + py * ay.y + pz * ay.z,
          px * az.x + py * az.y + pz * az.z,
          hx,
          hy,
          hz
        );
        const i = yBase + x;
        if (d < sdf[i]) sdf[i] = d;
      }
    }
  }
}

export function stampColliders(
  sdf: Float32Array,
  volume: VoxelVolume,
  colliders: readonly Collider[],
  extraBoxes: readonly Aabb[] = [],
  region?: VoxelIndexBox
): void {
  const box = region ? clipIndexBox(region, volume.resolution) : fullVoxelIndexBox(volume.resolution);
  if (!box) return;
  const minHalf = voxelSizeOf(volume) * 0.5;
  for (const collider of colliders) {
    if (collider.kind === 'aabb') stampAabbSdf(sdf, volume, collider.box, minHalf, box);
    else if (collider.kind === 'obb') stampObbSdf(sdf, volume, collider.obb, minHalf, box);
    else {
      const { a, b, c, offset } = collider.prism;
      stampTriangleSdf(
        sdf,
        volume,
        {
          a: { x: a.x + offset.x * 0.5, y: a.y + offset.y * 0.5, z: a.z + offset.z * 0.5 },
          b: { x: b.x + offset.x * 0.5, y: b.y + offset.y * 0.5, z: b.z + offset.z * 0.5 },
          c: { x: c.x + offset.x * 0.5, y: c.y + offset.y * 0.5, z: c.z + offset.z * 0.5 },
          half: Math.hypot(offset.x, offset.y, offset.z) * 0.5,
        },
        minHalf,
        box
      );
    }
  }
  for (const extra of extraBoxes) stampAabbSdf(sdf, volume, extra, minHalf, box);
}

export interface VoxelTriangle {
  a: Vec3;
  b: Vec3;
  c: Vec3;
  half: number;
}

export interface VoxelCapsule {
  a: Vec3;
  b: Vec3;
  radius: number;
}

function sdTriangle2(px: number, py: number, ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  const e0x = bx - ax;
  const e0y = by - ay;
  const v0x = px - ax;
  const v0y = py - ay;
  const e1x = cx - bx;
  const e1y = cy - by;
  const v1x = px - bx;
  const v1y = py - by;
  const e2x = ax - cx;
  const e2y = ay - cy;
  const v2x = px - cx;
  const v2y = py - cy;
  const d00 = e0x * e0x + e0y * e0y;
  const d11 = e1x * e1x + e1y * e1y;
  const d22 = e2x * e2x + e2y * e2y;
  const h0 = d00 > 1e-16 ? Math.min(1, Math.max(0, (v0x * e0x + v0y * e0y) / d00)) : 0;
  const h1 = d11 > 1e-16 ? Math.min(1, Math.max(0, (v1x * e1x + v1y * e1y) / d11)) : 0;
  const h2 = d22 > 1e-16 ? Math.min(1, Math.max(0, (v2x * e2x + v2y * e2y) / d22)) : 0;
  const pq0x = v0x - e0x * h0;
  const pq0y = v0y - e0y * h0;
  const pq1x = v1x - e1x * h1;
  const pq1y = v1y - e1y * h1;
  const pq2x = v2x - e2x * h2;
  const pq2y = v2y - e2y * h2;
  const s = Math.sign(e0x * e2y - e0y * e2x) || 1;
  const dx = Math.min(pq0x * pq0x + pq0y * pq0y, pq1x * pq1x + pq1y * pq1y, pq2x * pq2x + pq2y * pq2y);
  const dy = Math.min(s * (v0x * e0y - v0y * e0x), s * (v1x * e1y - v1y * e1x), s * (v2x * e2y - v2y * e2x));
  return -Math.sqrt(dx) * Math.sign(dy);
}

function sdTrianglePrism(
  point: Vec3,
  triangle: VoxelTriangle
): number {
  const abx = triangle.b.x - triangle.a.x;
  const aby = triangle.b.y - triangle.a.y;
  const abz = triangle.b.z - triangle.a.z;
  const acx = triangle.c.x - triangle.a.x;
  const acy = triangle.c.y - triangle.a.y;
  const acz = triangle.c.z - triangle.a.z;
  const nx = aby * acz - abz * acy;
  const ny = abz * acx - abx * acz;
  const nz = abx * acy - aby * acx;
  const nlen = Math.hypot(nx, ny, nz);
  if (nlen < 1e-9) return 1e5;
  const nxx = nx / nlen;
  const nyy = ny / nlen;
  const nzz = nz / nlen;
  const tlen = Math.hypot(abx, aby, abz);
  if (tlen < 1e-9) return 1e5;
  const tx = abx / tlen;
  const ty = aby / tlen;
  const tz = abz / tlen;
  const bx = nyy * tz - nzz * ty;
  const by = nzz * tx - nxx * tz;
  const bz = nxx * ty - nyy * tx;
  const px = point.x - triangle.a.x;
  const py = point.y - triangle.a.y;
  const pz = point.z - triangle.a.z;
  const d2 = sdTriangle2(
    px * tx + py * ty + pz * tz,
    px * bx + py * by + pz * bz,
    0,
    0,
    tlen,
    0,
    acx * tx + acy * ty + acz * tz,
    acx * bx + acy * by + acz * bz
  );
  const w0 = d2;
  const w1 = Math.abs(px * nxx + py * nyy + pz * nzz) - triangle.half;
  const ox = w0 > 0 ? w0 : 0;
  const oy = w1 > 0 ? w1 : 0;
  return Math.min(Math.max(w0, w1), 0) + Math.hypot(ox, oy);
}

function triangleBounds(triangle: VoxelTriangle): Aabb {
  const pad = triangle.half;
  return {
    min: {
      x: Math.min(triangle.a.x, triangle.b.x, triangle.c.x) - pad,
      y: Math.min(triangle.a.y, triangle.b.y, triangle.c.y) - pad,
      z: Math.min(triangle.a.z, triangle.b.z, triangle.c.z) - pad,
    },
    max: {
      x: Math.max(triangle.a.x, triangle.b.x, triangle.c.x) + pad,
      y: Math.max(triangle.a.y, triangle.b.y, triangle.c.y) + pad,
      z: Math.max(triangle.a.z, triangle.b.z, triangle.c.z) + pad,
    },
  };
}

function aabbIndexBox(volume: VoxelVolume, box: Aabb): VoxelIndexBox | null {
  const size = voxelSizeOf(volume);
  if (size < 1e-8) return null;
  return clipIndexBox(
    {
      x0: Math.floor((box.min.x - volume.origin.x) / size),
      x1: Math.ceil((box.max.x - volume.origin.x) / size),
      y0: Math.floor((box.min.y - volume.origin.y) / size),
      y1: Math.ceil((box.max.y - volume.origin.y) / size),
      z0: Math.floor((box.min.z - volume.origin.z) / size),
      z1: Math.ceil((box.max.z - volume.origin.z) / size),
    },
    volume.resolution
  );
}

function stampTriangleSdf(
  sdf: Float32Array,
  volume: VoxelVolume,
  triangle: VoxelTriangle,
  minHalf: number,
  region: VoxelIndexBox
): void {
  const half = triangle.half < minHalf ? minHalf : triangle.half;
  const thickened = { ...triangle, half };
  const bounds = aabbIndexBox(volume, triangleBounds(thickened));
  if (!bounds) return;
  const x0 = Math.max(region.x0, bounds.x0);
  const x1 = Math.min(region.x1, bounds.x1);
  const y0 = Math.max(region.y0, bounds.y0);
  const y1 = Math.min(region.y1, bounds.y1);
  const z0 = Math.max(region.z0, bounds.z0);
  const z1 = Math.min(region.z1, bounds.z1);
  if (x0 >= x1 || y0 >= y1 || z0 >= z1) return;
  const n = volume.resolution;
  const size = voxelSizeOf(volume);
  const ox = volume.origin.x + size * 0.5;
  const oy = volume.origin.y + size * 0.5;
  const oz = volume.origin.z + size * 0.5;
  const n2 = n * n;
  for (let z = z0; z < z1; z++) {
    const pz = oz + z * size;
    const zBase = z * n2;
    for (let y = y0; y < y1; y++) {
      const py = oy + y * size;
      const yBase = zBase + y * n;
      for (let x = x0; x < x1; x++) {
        const d = sdTrianglePrism({ x: ox + x * size, y: py, z: pz }, thickened);
        const i = yBase + x;
        if (d < sdf[i]) sdf[i] = d;
      }
    }
  }
}

function stampCapsuleSdf(
  sdf: Float32Array,
  volume: VoxelVolume,
  capsule: VoxelCapsule,
  minHalf: number,
  region: VoxelIndexBox
): void {
  const radius = capsule.radius < minHalf ? minHalf : capsule.radius;
  const obb: Obb = {
    center: {
      x: (capsule.a.x + capsule.b.x) * 0.5,
      y: (capsule.a.y + capsule.b.y) * 0.5,
      z: (capsule.a.z + capsule.b.z) * 0.5,
    },
    half: {
      x: Math.hypot(capsule.b.x - capsule.a.x, capsule.b.y - capsule.a.y, capsule.b.z - capsule.a.z) * 0.5 + radius,
      y: radius,
      z: radius,
    },
    axes: [
      { x: 1, y: 0, z: 0 },
      { x: 0, y: 1, z: 0 },
      { x: 0, y: 0, z: 1 },
    ],
  };
  const dx = capsule.b.x - capsule.a.x;
  const dy = capsule.b.y - capsule.a.y;
  const dz = capsule.b.z - capsule.a.z;
  const len = Math.hypot(dx, dy, dz);
  if (len >= 1e-9) {
    const ax = { x: dx / len, y: dy / len, z: dz / len };
    const helper = Math.abs(ax.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
    const hx = helper.y * ax.z - helper.z * ax.y;
    const hy = helper.z * ax.x - helper.x * ax.z;
    const hz = helper.x * ax.y - helper.y * ax.x;
    const hlen = Math.hypot(hx, hy, hz);
    const right = hlen < 1e-9 ? { x: 0, y: 1, z: 0 } : { x: hx / hlen, y: hy / hlen, z: hz / hlen };
    const up = {
      x: ax.y * right.z - ax.z * right.y,
      y: ax.z * right.x - ax.x * right.z,
      z: ax.x * right.y - ax.y * right.x,
    };
    obb.half.x = len * 0.5 + radius;
    obb.axes = [ax, right, up];
  }
  stampObbSdf(sdf, volume, obb, minHalf, region);
}

export interface VoxelStampLeftover {
  planes?: ReadonlyArray<Obb>;
  triangles?: ReadonlyArray<VoxelTriangle>;
  braces?: ReadonlyArray<VoxelCapsule>;
}

/** GPU の surfaceBias（max(0.04, voxel * 0.75)）と同じ。表面より内側に収めて自己影を避ける。 */
export function leftoverStampErosion(volume: VoxelVolume): number {
  return Math.max(0.04, voxelSizeOf(volume) * 0.75);
}

export function stampLeftoverPrimitives(
  sdf: Float32Array,
  volume: VoxelVolume,
  leftover: VoxelStampLeftover,
  region?: VoxelIndexBox
): void {
  const box = region ? clipIndexBox(region, volume.resolution) : fullVoxelIndexBox(volume.resolution);
  if (!box) return;
  const erode = leftoverStampErosion(volume);
  for (const plane of leftover.planes ?? []) {
    const hx = plane.half.x - erode;
    const hy = plane.half.y - erode;
    const hz = plane.half.z - erode;
    if (hx <= 0 || hy <= 0 || hz <= 0) continue;
    stampObbSdf(sdf, volume, { ...plane, half: { x: hx, y: hy, z: hz } }, 0, box);
  }
  for (const triangle of leftover.triangles ?? []) {
    const half = triangle.half - erode;
    if (half <= 0) continue;
    stampTriangleSdf(sdf, volume, { ...triangle, half }, 0, box);
  }
  for (const brace of leftover.braces ?? []) {
    const radius = brace.radius - erode;
    if (radius <= 0) continue;
    stampCapsuleSdf(sdf, volume, { ...brace, radius }, 0, box);
  }
}

/**
 * 原点がボクセル単位で動いた静的 SDF を、重なりをコピーして新規スラブだけ焼き直す。
 * 重なりが無い・格子が合わないときは null（フル再計算が必要）。
 */
export function advanceVoxelSdf(
  previousSdf: Float32Array,
  previousVolume: VoxelVolume,
  nextVolume: VoxelVolume,
  colliders: readonly Collider[]
): Float32Array | null {
  const shift = voxelOriginShift(previousVolume, nextVolume);
  if (!shift) return null;
  const sdf = copyShiftedVoxelSdf(previousSdf, previousVolume, nextVolume);
  if (!sdf) return null;
  const previousBounds = volumeBounds(previousVolume);
  const margin = voxelSizeOf(previousVolume) * 0.5;
  const innerPrevious: Aabb = {
    min: {
      x: previousBounds.min.x + margin,
      y: previousBounds.min.y + margin,
      z: previousBounds.min.z + margin,
    },
    max: {
      x: previousBounds.max.x - margin,
      y: previousBounds.max.y - margin,
      z: previousBounds.max.z - margin,
    },
  };
  const innerValid =
    innerPrevious.min.x < innerPrevious.max.x &&
    innerPrevious.min.y < innerPrevious.max.y &&
    innerPrevious.min.z < innerPrevious.max.z;
  const kept: Collider[] = [];
  const entering: Collider[] = [];
  for (const collider of colliders) {
    if (innerValid && aabbIntersects(colliderBounds(collider), innerPrevious)) kept.push(collider);
    else entering.push(collider);
  }
  for (const region of uncoveredVoxelRegions(
    nextVolume.resolution,
    shift.ox,
    shift.oy,
    shift.oz
  )) {
    stampColliders(sdf, nextVolume, kept, [], region);
  }
  stampColliders(sdf, nextVolume, entering);
  return sdf;
}

export function buildVoxelSdf(
  volume: VoxelVolume,
  colliders: readonly Collider[],
  extraBoxes: readonly Aabb[] = []
): Float32Array {
  const sdf = new Float32Array(volumeVoxelCount(volume.resolution));
  sdf.fill(volume.extent);
  stampColliders(sdf, volume, colliders, extraBoxes);
  return sdf;
}

export function sampleSdfNearest(sdf: Float32Array, volume: VoxelVolume, point: Vec3): number {
  const size = voxelSizeOf(volume);
  const x = Math.floor((point.x - volume.origin.x) / size);
  const y = Math.floor((point.y - volume.origin.y) / size);
  const z = Math.floor((point.z - volume.origin.z) / size);
  const last = volume.resolution - 1;
  if (x < 0 || y < 0 || z < 0 || x > last || y > last || z > last) {
    return volume.extent;
  }
  return sdf[volumeIndex(volume, x, y, z)];
}
